import PageSEO from '@/components/PageSEO';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Users, Target, Award, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cities } from '@/data/localServiceData';
import ServiceAreaMapReal from '@/components/ServiceAreaMapReal';

export const AboutPage = () => {
  const values = [
    {
      icon: CheckCircle,
      title: 'Quality Craftsmanship',
      description: 'Every project receives meticulous attention to detail and precision.',
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'We listen to your goals and include you in every step of the design process.',
    },
    {
      icon: Target,
      title: 'Clear Communication',
      description: 'We over-communicate on expectations and progress, doing what we say we will do.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Extensive training ensures our crew creates the tightest possible gap reveals.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="About Us" description="Comal Glass Company is a full-service glass provider in Bulverde, TX. We serve New Braunfels, Boerne, Canyon Lake, and the entire Texas Hill Country with custom glass installations." path="/about" />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6">About Us</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Leading Provider of <span className="text-gradient-cyan">Premium Glass Solutions</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Comal Glass Company is the leading provider of high-quality frameless shower glass, mirrors, and architectural glass solutions. We combine exceptional products with caring service to create stunning results.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Our <span className="text-gradient-cyan">Story</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Since 2022, Comal Glass Company has been transforming spaces throughout Texas with custom glass solutions. What started as a passion for precision craftsmanship has grown into a full-service glass company trusted by homeowners and businesses alike.
                </p>
                <p>
                  Our team of extensively trained professionals brings expertise, creativity, and an unwavering commitment to excellence to every project. From frameless shower enclosures to commercial storefronts, we approach each installation as an opportunity to create something truly remarkable.
                </p>
                <p>
                  We believe in doing things right - from our initial consultation through final installation and beyond. That's why we follow the campsite principle: "Leave it better than you found it." Every job site we work on is left cleaner and better than when we arrived.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-lg shadow-xl overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/nB_ADBFOAiY"
                  title="Comal Glass Company Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team in Action Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="/images/mirrors/mirror_installation.jpg"
                alt="Comal Glass team installing a mirror"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <Badge className="mb-4">Our Team</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Expert <span className="text-gradient-cyan">Installation</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Our skilled installation team brings years of experience and a commitment to perfection to every project. From custom mirrors to frameless shower enclosures, we handle each installation with care and precision.
                </p>
                <p>
                  We take pride in our work and treat every home as if it were our own. Our team members are trained to deliver exceptional results while maintaining clean, organized job sites throughout the installation process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Our Values</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              What Sets Us <span className="text-gradient-cyan">Apart</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our commitment to excellence is reflected in everything we do.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6 flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                    <p className="text-muted-foreground text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Why Choose <span className="text-gradient-cyan">Comal Glass?</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold mb-2">Professional, Customer-Focused Crew</h3>
                  <p className="text-muted-foreground text-sm">
                    Friendly and professional crew members that take their time to ensure everything from initial consultation to final installation is handled efficiently and thoroughly.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold mb-2">Clean Job Sites</h3>
                  <p className="text-muted-foreground text-sm">
                    "Leave it better than you found it" - We use this campsite principle to guide how we approach every job site.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold mb-2">Ultra-Tight Tolerances</h3>
                  <p className="text-muted-foreground text-sm">
                    All crew members are trained extensively to create the tightest possible gap reveals on shower glass installs to make your new shower stand out as a work of art.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold mb-2">Clear & Consistent Communication</h3>
                  <p className="text-muted-foreground text-sm">
                    We listen to your goals and include you in the design process. We over-communicate on expectations and progress.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Service Area Map */}
      <section className="py-20">
        <div className="container-custom">
          <ServiceAreaMapReal />
        </div>
      </section>

      {/* Areas We Serve Section */}
      <section className="py-20 bg-gradient-subtle" data-testid="areas-we-serve">
        <div className="container-custom">
          <div className="text-center mb-12">
            <Badge className="mb-4">
              <MapPin className="h-3 w-3 mr-1" />
              Service Areas
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Areas We <span className="text-gradient-cyan">Serve</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Based in Bulverde, we proudly serve homeowners and builders across the Texas Hill Country.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {Object.entries(cities).map(([slug, city]) => (
              <Card key={slug} className="border-none shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold mb-1">{city.name}, {city.state}</h3>
                      <p className="text-xs text-muted-foreground mb-3">{city.county} &middot; Also serving {city.nearbyAreas}</p>
                      <div className="flex flex-wrap gap-1.5">
                        <Link to={`/shower-glass-${slug}`} className="text-xs text-primary hover:underline">Shower Glass</Link>
                        <span className="text-xs text-muted-foreground">&middot;</span>
                        <Link to={`/wine-enclosures-${slug}`} className="text-xs text-primary hover:underline">Wine Enclosures</Link>
                        <span className="text-xs text-muted-foreground">&middot;</span>
                        <Link to={`/mirrors-${slug}`} className="text-xs text-primary hover:underline">Mirrors</Link>
                        <span className="text-xs text-muted-foreground">&middot;</span>
                        <Link to={`/glass-railings-${slug}`} className="text-xs text-primary hover:underline">Railings</Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Associations Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h3 className="text-xl font-semibold mb-8 text-muted-foreground">In Association With</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center justify-items-center">
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/NAHB.png" 
                alt="NAHB - National Association of Home Builders" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/GNHBA.png" 
                alt="GNBHBA - Greater New Braunfels Home Builders Association" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/TAB.png" 
                alt="TAB - Texas Association of Builders" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/NGA.png" 
                alt="NGA - National Glass Association" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/TGA.jpg" 
                alt="TGA - Texas Glass Association" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
